import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { StandardIds } from '../../../shared-services/crm-services/field.service';
import { FieldValueInterface } from '@vendasta/crm';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { BlankValueComponent, CustomCellComponent } from '@vendasta/galaxy/table';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { of, withLatestFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CategoryInterface } from '@vendasta/category';
import { CategoryService } from '../../../shared-services/category.service';

const NO_VALUE_FOR_CATEGORY = '<NO_VALUE_FOR_CATEGORY>';

@Component({
  template: `
    @if (categories(); as categories) {
      @if (categories !== NO_VALUE_FOR_CATEGORY) {
        <div class="category tags">
          @let _categoryNames = categoryNames();
          <glxy-badge [glxyTooltip]="_categoryNames.join(', ')">{{ _categoryNames[0] }}</glxy-badge>
          @if (_categoryNames.length === 2) {
            <glxy-badge [glxyTooltip]="_categoryNames.join(', ')">
              {{ _categoryNames[1] }}
            </glxy-badge>
          }
          @if (_categoryNames.length > 2) {
            <glxy-badge [glxyTooltip]="_categoryNames.join(', ')"> +{{ _categoryNames.length - 1 }} </glxy-badge>
          }
        </div>
      } @else {
        <glxy-blank-value></glxy-blank-value>
      }
    } @else {
      <div class="stencil-shimmer"></div>
    }
  `,
  styleUrls: ['./categories.component.scss'],
  imports: [CommonModule, BlankValueComponent, GalaxyBadgeModule, GalaxyTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesCellComponent extends CustomCellComponent {
  NO_VALUE_FOR_CATEGORY = NO_VALUE_FOR_CATEGORY;
  fieldValues: WritableSignal<(FieldValueInterface | null)[]> = signal([]);
  set value(newValues: (FieldValueInterface | null)[]) {
    this.fieldValues.set(newValues);
  }
  categories: WritableSignal<CategoryInterface[] | '<NO_VALUE_FOR_CATEGORY>' | null> = signal(null);
  categoryNames = computed(() => {
    const categories = this.categories();
    if (categories === NO_VALUE_FOR_CATEGORY) {
      return [];
    }
    return (categories ?? []).map((category) => category.name);
  });

  constructor(private readonly categoryService: CategoryService) {
    super();
    toObservable(this.fieldValues)
      .pipe(
        switchMap((fieldValues) => {
          const categoryFieldValue =
            fieldValues.find((fieldValue) => fieldValue?.fieldId === StandardIds.CompanyCategoryIDs) ?? null;
          if (!categoryFieldValue) {
            return of(NO_VALUE_FOR_CATEGORY).pipe(withLatestFrom(of([])));
          }
          const categoryIds = categoryFieldValue.stringValues?.values ?? [];
          if (!categoryIds.length) {
            return of(NO_VALUE_FOR_CATEGORY).pipe(withLatestFrom(of(categoryIds)));
          }
          return this.categoryService.getMultiCategories(categoryIds).pipe(withLatestFrom(of(categoryIds)));
        }),
        takeUntilDestroyed(),
      )
      .subscribe(([result, categoryIds]) => {
        if (result === NO_VALUE_FOR_CATEGORY) {
          this.categories.set(NO_VALUE_FOR_CATEGORY);
          return;
        }
        const categories = result as CategoryInterface[];
        const combinedFoundAndNotFound = [];
        for (const categoryId of categoryIds) {
          const foundCategory = categories.find((category) => category.externalId === categoryId);
          if (foundCategory) {
            combinedFoundAndNotFound.push(foundCategory);
          } else {
            combinedFoundAndNotFound.push({
              name: categoryId,
              externalId: categoryId,
            });
          }
        }
        this.categories.set(combinedFoundAndNotFound);
      });
  }
}
